import awesomeDebouncePromise from 'awesome-debounce-promise';
import { useState } from 'react';
import { useAsync, type UseAsyncReturn } from 'react-async-hook';
import useConstant from 'use-constant';

import type {
  EmailLookupResult,
  Environment,
  Experience,
} from '../../../../../types';
import isValidEmail from '../../../../../utils/isValidEmail';
import { performEmailLookup } from '../../../../../utils/performEmailLookup';
import { useAppContext } from '../../../../AppContext';

// https://stackoverflow.com/questions/23123138/perform-debounce-in-react-js
const useDebouncedLookup = (
  environment: Environment,
  experience: Experience,
  initialEmail = '',
): {
  emailAddress: string;
  setEmailAddress: React.Dispatch<React.SetStateAction<string>>;
  emailLookupResult: EmailLookupResult;
} => {
  // Handle the input text state
  const [emailAddress, setEmailAddress] = useState(initialEmail);

  // Debounce the original search async function
  const debouncedSearchFunction = useConstant(() =>
    awesomeDebouncePromise(performEmailLookup, 300),
  );

  const { enableRealmHint, brand, locale } = useAppContext();

  // The async callback is run each time the text changes,
  // but as the search function is debounced, it does not
  // fire a new request on each keystroke
  const emailLookupPromise = useAsync(async () => {
    if (!isValidEmail(emailAddress) || !enableRealmHint) {
      return Promise.resolve({} as EmailLookupResult);
    }

    return debouncedSearchFunction(
      emailAddress,
      environment,
      experience,
      locale,
      brand,
    );
  }, [debouncedSearchFunction, emailAddress]);

  const deriveResult = (): EmailLookupResult => {
    if (emailLookupPromise.error) {
      return {};
    }
    if (emailLookupPromise.loading) {
      return {};
    }
    return emailLookupPromise.result!;
  };

  const emailLookupResult = deriveResult();

  return {
    emailAddress,
    setEmailAddress,
    emailLookupResult,
  };
};

export type DebouncedLookupResult = UseAsyncReturn<
  EmailLookupResult,
  Array<
    | string
    | ((
        email: string,
        clientOrigin: string,
        experience: Experience,
      ) => Promise<EmailLookupResult>)
  >
>;

export default useDebouncedLookup;
